<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

const features = inject("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Submit button editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the submit button",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, { data: definitionData.value });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Submit Button",
    icon: "lucide:send",
  },
});

interface FormContext {
  id: string;
  validateAll: () => Promise<boolean>;
}
const formContext = inject<FormContext | null>("form-context", null);

const alignmentClass = computed(() => {
  switch (definitionData.value.alignment) {
    case "left":
      return "justify-start";
    case "right":
      return "justify-end";
    case "center":
    default:
      return "justify-center";
  }
});

const buttonSize = computed(() => {
  switch (definitionData.value.size) {
    case "sm":
      return "sm";
    case "lg":
      return "lg";
    case "md":
    default:
      return "default";
  }
});

const buttonVariant = computed(() => definitionData.value.variant || "default");

const { busy, execute: onClick } = useBlockCompletionAction({
  editor: () => features.editor,
  automationPath: () => definitionData.value?.automationPath,
  navigationPath: () => definitionData.value?.reportPath,
  navigationDelaySeconds: () => definitionData.value?.delaySeconds,
  beforeExecute: async () => {
    if (formContext) {
      const ok = await formContext.validateAll();
      if (!ok) {
        return false;
      }
    }

    return true;
  },
});
</script>

<template>
  <div :class="cn('flex w-full', alignmentClass, $attrs.class ?? '')">
    <Button
      :variant="buttonVariant as any"
      :size="buttonSize as any"
      :disabled="busy"
      @click="onClick"
      type="button"
    >
      <icon mode="svg" v-if="busy" name="lucide:loader-circle" class="animate-spin" />
      <template v-else>
        <icon mode="svg"
          v-if="definitionData.icon && definitionData.iconPosition !== 'right'"
          :name="definitionData.icon"
          class="text-base"
        />
        <span>{{ definitionData.buttonText || "Submit" }}</span>
        <icon mode="svg"
          v-if="definitionData.icon && definitionData.iconPosition === 'right'"
          :name="definitionData.icon"
          class="text-base"
        />
      </template>
    </Button>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.FORM_SUBMIT,
  editor: () => import("#bb/components/editor/assessment/form-submit.vue"),
  icon: "lucide:send",
  type: "Submit Button",
  description:
    "Customizable submit button. Validates an enclosing form, runs an optional automation, then redirects.",
  default: {
    buttonText: "Submit",
    icon: "lucide:send",
    iconPosition: "left",
    size: "md",
    alignment: "center",
    variant: "default",
    automationPath: "",
    reportPath: "/reports/me",
    delaySeconds: 0,
  },
  categories: ["Assessment"],
  order: 10051,
};
</script>
